//////////////////
// IMPORTS
//////////////////
// schema
// libraries
import * as ISchema from "@schema";
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { createFragmentContainer } from "react-relay";
// event-personal-calendar-info imports
import { eventPersonalCalendarInfoFragments } from "./EventPersonalCalendarInfo.gql";
// state
import { IReduxState } from "@reducers/root.reducer";
import { setActiveViewFeed } from "@actions/chrome-extension.actions";
// other imports
// util
import { overrideSyncStatusForUserEvent } from "@mutations";
import { snackBarOpen } from "@actions/snack-bar.actions";
import { track, TrackingEvents } from "@util/analytics.util";
import { articleUrls } from "@clockwise/client-commons/src/constants/help-center";

// material-ui
import Button from "@material-ui/core/Button";
import { Delete } from "@material-ui/icons";
import { getCurrentEnvironment } from "@src/state/relay-environment";

import { greys, colors } from "@clockwise/web-commons/src/styles/color.styles";
import spacing from "@clockwise/web-commons/src/styles/spacing.styles";
import { makeStyles } from "@material-ui/styles";
import { CalloutCard } from "@clockwise/web-commons/src/ui/callout-card";
import { isError } from "@src/util/errorable.util";

const useStyles = makeStyles({
  textProminent: {
    fontSize: 12,
    lineHeight: 1.3,
    color: colors.blueCool.standard,
    fontWeight: 600,
    textTransform: "uppercase",
  },
  flatButton: {
    lineHeight: "24px",
    padding: "0 24px",
    paddingLeft: 8,
    paddingTop: 2,
    color: greys.slate,
  },
  flatButtonIcon: {
    marginRight: spacing.std,
    paddingBottom: 2,
  },
});

//////////////////
// COMPONENT
//////////////////
type EventPersonalCalendarInfoProps = {
  eventParent: ISchema.IEventParent;
  isOrganizer: boolean;
};

const EventPersonalCalendarInfoCmpt = ({
  eventParent,
  isOrganizer,
}: EventPersonalCalendarInfoProps) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const erroredSyncedOriginEvent = isError(eventParent.syncedOriginEventErrorable);
  const eventDeleteButtonClicked = useSelector(
    (state: IReduxState) => state.webExtension.eventDeleteButtonClicked,
  );

  const onOverrideSuccess = () => {
    dispatch(snackBarOpen("This event will no longer be synced from your personal calendar"));
    dispatch(setActiveViewFeed());
  };

  const onOverrideFailure = () => {
    dispatch(snackBarOpen("Sorry, there was a problem removing your synced event"));
  };

  const deleteSyncedEvent = () => {
    track(TrackingEvents.EVENT.REMOVE_SYNCED_PERSONAL_CALENDAR_EVENT);
    const externalEventId = eventParent.externalEventId;
    overrideSyncStatusForUserEvent(
      getCurrentEnvironment(),
      { externalEventId },
      onOverrideSuccess,
      onOverrideFailure,
    );
  };

  if (eventDeleteButtonClicked) {
    deleteSyncedEvent();
  }

  const onLearnMore = () => {
    track(TrackingEvents.HELP.PERSONAL_CALENDAR_SYNC);
  };

  if (!isOrganizer) {
    return (
      <CalloutCard
        sentiment="info"
        content={{
          header: "Personal Calendar Event",
          body: (
            <p>Clockwise automatically synced this event from another user's personal calendar.</p>
          ),
        }}
      />
    );
  }

  if (erroredSyncedOriginEvent) {
    return (
      <CalloutCard
        sentiment="info"
        content={{
          header: "Personal Calendar Event",
          body: <p>Sorry, there was an error fetching your event from your personal calendar.</p>,
        }}
      />
    );
  }

  return (
    <>
      <CalloutCard
        sentiment="info"
        content={{
          header: "Personal Calendar Event",
          body: (
            <>
              <p>This event was automatically synced from your personal calendar.</p>
              <p className="cw-mt-1">
                Only you can see the details of this event. Coworkers with access to your calendar
                will see an event with a title of "Busy (via Clockwise)."&nbsp;
              </p>
              <div className="cw-mt-1">
                <a target="_blank" href={articleUrls.personalCalSync} onClick={onLearnMore}>
                  Learn more
                </a>
              </div>
            </>
          ),
        }}
      />
      <Button
        className="cw-mt-4 cw-text-slate-standard"
        onClick={deleteSyncedEvent}
        variant="outlined"
        fullWidth
      >
        <Delete className={classes.flatButtonIcon} />
        Remove Synced Event
      </Button>
    </>
  );
};

//////////////////
// RELAY
//////////////////
export const EventPersonalCalendarInfo = createFragmentContainer<EventPersonalCalendarInfoProps>(
  EventPersonalCalendarInfoCmpt,
  eventPersonalCalendarInfoFragments,
);
